import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft, Plus, Printer, Pencil, ShieldAlert, Clock,
  Phone, MapPin, CalendarDays, UserRound, Stethoscope, FileText,
  Pill, Activity, PenLine, Archive, ArchiveRestore, Siren,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import ObservationForm from "@/components/ObservationForm";
import AddPatientDialog from "@/components/AddPatientDialog";
import ObservationsSection from "@/components/patient/ObservationsSection";
import MedicationsSection from "@/components/patient/MedicationsSection";
import InjuriesSection from "@/components/patient/InjuriesSection";
import NotesSection from "@/components/patient/NotesSection";
import SignaturesSection from "@/components/patient/SignaturesSection";
import TransportSection from "@/components/patient/TransportSection";
import AdviceSection from "@/components/patient/AdviceSection";
import EcgSection from "@/components/patient/EcgSection";
import { TriageBadge, ageFromDob, OUTCOME_LABELS, fmt } from "@/lib/clinical";

function scrollToSection(id) {
  document.querySelector(`[data-testid="${id}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function PatientDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [observations, setObservations] = useState([]);
  const [notes, setNotes] = useState([]);
  const [obsOpen, setObsOpen] = useState(false);
  const [editObs, setEditObs] = useState(null);
  const [editPatient, setEditPatient] = useState(false);
  const [saving, setSaving] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [medications, setMedications] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [medForm, setMedForm] = useState({ medication: "", dosage: "", batch_number: "", expiry_date: "", notes: "" });
  const [sigName, setSigName] = useState("");
  const [sigNote, setSigNote] = useState("");
  const [sigData, setSigData] = useState(null);
  const [dischargeOpen, setDischargeOpen] = useState(false);
  const [dischargeOutcome, setDischargeOutcome] = useState("returned_to_event");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [medOptions, setMedOptions] = useState([]);
  const [sgOpen, setSgOpen] = useState(false);
  const [sgForm, setSgForm] = useState({ concerns: "", who_involved: "", action_taken: "" });
  const [hospital, setHospital] = useState("");

  useEffect(() => { setHospital(patient?.hospital || ""); }, [patient?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    try {
      const [p, o, n, m, s] = await Promise.all([
        api.get(`/patients/${id}`),
        api.get(`/patients/${id}/observations`),
        api.get(`/patients/${id}/notes`),
        api.get(`/patients/${id}/medications`),
        api.get(`/patients/${id}/signatures`),
      ]);
      setPatient(p.data);
      setObservations(o.data);
      setNotes(n.data);
      setMedications(m.data);
      setSignatures(s.data);
    } catch (e) {
      toast.error(formatApiError(e));
      navigate("/dashboard");
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const loadMeds = useCallback(() => {
    api.get("/med-list").then((r) => setMedOptions(r.data)).catch((e) => console.debug("Med list unavailable:", e?.message));
  }, []);

  useEffect(() => { loadMeds(); }, [loadMeds]);

  const saveObservation = async (payload) => {
    setSaving(true);
    try {
      await api.post(`/patients/${id}/observations`, payload);
      toast.success("Observation recorded");
      setObsOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const saveObservationEdit = async (payload) => {
    setSaving(true);
    try {
      await api.patch(`/observations/${editObs.id}`, payload);
      toast.success("Observation updated");
      setEditObs(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const saveNote = async () => {
    if (!noteText.trim()) return;
    setSaving(true);
    try {
      await api.post(`/patients/${id}/notes`, { text: noteText });
      setNoteText("");
      toast.success("Note added");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await api.delete(`/notes/${noteId}`);
      toast.success("Note deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openDischarge = () => {
    setDischargeOutcome("returned_to_event");
    setDischargeOpen(true);
  };

  const confirmDischarge = async () => {
    try {
      await api.patch(`/patients/${id}`, { status: "discharged", discharge_outcome: dischargeOutcome });
      toast.success("Patient discharged");
      setDischargeOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const reopenCase = async () => {
    try {
      await api.patch(`/patients/${id}`, { status: "active" });
      toast.success("Case reopened");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deletePatient = async () => {
    try {
      await api.delete(`/patients/${id}`);
      toast.success("Patient record deleted");
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleInjury = async (part) => {
    const current = patient.injuries || [];
    const next = current.includes(part) ? current.filter((x) => x !== part) : [...current, part];
    try {
      const { data } = await api.patch(`/patients/${id}`, { injuries: next });
      setPatient(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveSafeguarding = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/patients/${id}/safeguarding`, sgForm);
      toast.success(data.message);
      setSgOpen(false);
      setSgForm({ concerns: "", who_involved: "", action_taken: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const saveHospital = async () => {
    if ((patient?.hospital || "") === hospital.trim()) return;
    try {
      const { data } = await api.patch(`/patients/${id}`, { hospital: hospital.trim() });
      setPatient(data);
      if (hospital.trim()) toast.success("Hospital saved");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveMedication = async (e) => {
    e.preventDefault();
    if (!medForm.medication.trim()) return;
    setSaving(true);
    try {
      await api.post(`/patients/${id}/medications`, medForm);
      setMedForm({ medication: "", dosage: "", batch_number: "", expiry_date: "", notes: "" });
      toast.success("Medication recorded");
      load();
      loadMeds();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteMedication = async (mid) => {
    try {
      await api.delete(`/medications/${mid}`);
      toast.success("Medication record deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveSignature = async () => {
    if (!sigName.trim() || !sigData) return;
    setSaving(true);
    try {
      await api.post(`/patients/${id}/signatures`, {
        patient_name: sigName.trim(),
        signature_data: sigData,
        note: sigNote,
      });
      setSigName("");
      setSigNote("");
      setSigData(null);
      toast.success("Refusal form saved");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleArchive = async () => {
    try {
      const { data } = await api.patch(`/patients/${id}/archive`, { archived: !patient.archived });
      toast.success(data.message);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setLocation = async (loc) => {
    try {
      await api.patch(`/patients/${id}`, { location: loc });
      toast.success(loc ? `Location: ${loc}` : "Location cleared");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const sendAlert = async () => {
    if (!window.confirm(`Send a SERIOUS PATIENT alert to everyone on ${patient.event_location || "this event"}?`)) return;
    try {
      const { data } = await api.post(`/patients/${id}/alert`);
      toast.success(data.message);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!patient) {
    return <div className="min-h-screen grid place-items-center bg-[#F4F7FA] text-slate-400 text-sm">Loading patient…</div>;
  }

  const age = ageFromDob(patient.dob);

  return (
    <div className="min-h-screen bg-[#F4F7FA]" data-testid="patient-detail-page">
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200 px-4 sm:px-8 py-3 flex justify-between items-center shadow-sm">
        <button onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard-btn"
          className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-[#005EB8] transition-colors min-h-[44px]">
          <ChevronLeft className="h-4 w-4" /> Patients
        </button>
        <div className="flex items-center gap-2">
          <button onClick={() => setEditPatient(true)} data-testid="edit-patient-btn"
            className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold px-3 py-2.5 rounded-lg border border-slate-300 text-[#0F2547] hover:border-[#005EB8] hover:text-[#005EB8] transition-colors min-h-[44px]">
            <Pencil className="h-4 w-4" /> <span className="hidden sm:inline">Edit details</span>
          </button>
          <button onClick={sendAlert} data-testid="patient-alert-btn"
            title="Alert everyone on this event — serious patient"
            className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold px-3 py-2.5 rounded-lg bg-[#D5281B] hover:bg-[#b32117] transition-colors text-white min-h-[44px] animate-pulse hover:animate-none">
            <Siren className="h-4 w-4" /> <span className="hidden sm:inline">ALERT</span>
          </button>
          <select
            value={patient.location || ""}
            onChange={(e) => setLocation(e.target.value)}
            data-testid="patient-location-select"
            title="Where the patient is right now"
            className="h-11 rounded-lg border border-slate-300 px-2 text-xs sm:text-sm font-semibold text-[#0F2547] bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]"
          >
            <option value="">Location…</option>
            {["Bay 1", "Bay 2", "Bay 3", "Bay 4", "Bay 5", "Out on field", "Ambulance", "Control"].map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
          {user?.role === "admin" && (
            <button onClick={toggleArchive} data-testid="archive-patient-btn"
              title={patient.archived ? "Restore to dashboard" : "Archive — hides from dashboard, record kept"}
              className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold px-3 py-2.5 rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-50 transition-colors min-h-[44px]">
              {patient.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
              <span className="hidden sm:inline">{patient.archived ? "Restore" : "Archive"}</span>
            </button>
          )}
          <Link to={`/patients/${id}/report`} data-testid="export-pdf-button"
            className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold px-3 sm:px-4 py-2.5 rounded-lg bg-[#0F2547] text-white hover:bg-[#173766] transition-colors min-h-[44px]">
            <Printer className="h-4 w-4" /> <span className="hidden sm:inline">Export Report</span>
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 sm:p-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: demographics */}
        <div className="lg:col-span-4 space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="patient-demographics-section">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="font-heading text-2xl font-extrabold text-[#0F2547] leading-tight" data-testid="patient-name">
                  {patient.first_name} {patient.middle_names ? `${patient.middle_names} ` : ""}{patient.last_name}
                </h1>
                <p className="text-xs text-slate-500 mt-1">
                  Lead: <span className="font-semibold text-[#0F2547]">{patient.created_by_name}</span>
                </p>
                {patient.last_treated_by_name && (
                  <p className="text-xs text-slate-500" data-testid="last-treated-by">
                    Last treated by <span className="font-semibold text-[#0F2547]">{patient.last_treated_by_name}</span>
                  </p>
                )}
                {patient.status === "discharged" && patient.discharge_outcome && (
                  <p className="text-xs font-semibold text-slate-600 mt-1" data-testid="discharge-outcome-label">
                    Outcome: {OUTCOME_LABELS[patient.discharge_outcome] || patient.discharge_outcome}
                  </p>
                )}
              </div>
              {patient.status === "discharged" ? (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold border bg-slate-100 text-slate-500 border-slate-200">Discharged</span>
              ) : (
                <TriageBadge triage={patient.triage} />
              )}
            </div>
            <dl className="space-y-3 text-sm">
              <div className="flex items-center gap-2 text-slate-700">
                <CalendarDays className="h-4 w-4 text-[#005EB8] shrink-0" />
                <span>{patient.dob || "DOB not recorded"}{age != null && ` · ${age} years old`}</span>
              </div>
              {patient.arrived_at && (
                <div className="flex items-center gap-2 text-slate-700" data-testid="patient-arrived-at">
                  <Clock className="h-4 w-4 text-[#005EB8] shrink-0" />
                  <span>Arrived {fmt(patient.arrived_at)}</span>
                </div>
              )}
              {patient.phone && (
                <div className="flex items-center gap-2 text-slate-700">
                  <Phone className="h-4 w-4 text-[#005EB8] shrink-0" /><span>{patient.phone}</span>
                </div>
              )}
              {patient.address && (
                <div className="flex items-start gap-2 text-slate-700">
                  <MapPin className="h-4 w-4 text-[#005EB8] shrink-0 mt-0.5" /><span>{patient.address}</span>
                </div>
              )}
              {patient.nhs_number && (
                <div className="flex items-center gap-2 text-slate-700">
                  <FileText className="h-4 w-4 text-[#005EB8] shrink-0" /><span>NHS No. {patient.nhs_number}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-slate-700">
                <MapPin className="h-4 w-4 text-[#D5281B] shrink-0" />
                <input value={hospital} onChange={(e) => setHospital(e.target.value)} onBlur={saveHospital}
                  placeholder="Hospital taken to… (check the medical plan)" data-testid="patient-hospital" title="Which hospital the patient was taken to — the event's medical plan lists the nearest"
                  className="flex-1 min-w-0 h-9 rounded-lg border border-slate-300 px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
              </div>
              {patient.event_location && (
                <div className="flex items-center gap-2 text-slate-700">
                  <Stethoscope className="h-4 w-4 text-[#005EB8] shrink-0" /><span>{patient.event_location}</span>
                </div>
              )}
              {patient.w3w && (
                <div className="flex items-center gap-2 text-slate-700">
                  <MapPin className="h-4 w-4 text-[#E11F26] shrink-0" />
                  <a href={`https://what3words.com/${patient.w3w}`} target="_blank" rel="noreferrer"
                    data-testid="patient-w3w-link"
                    className="font-bold text-[#E11F26] hover:underline">///{patient.w3w}</a>
                </div>
              )}
              {patient.emergency_contact_name && (
                <div className="flex items-center gap-2 text-slate-700">
                  <UserRound className="h-4 w-4 text-[#005EB8] shrink-0" />
                  <span>Emergency: {patient.emergency_contact_name}{patient.emergency_contact_phone && ` · ${patient.emergency_contact_phone}`}</span>
                </div>
              )}
            </dl>
            <button onClick={patient.status === "discharged" ? reopenCase : openDischarge} data-testid="toggle-discharge-btn"
              className="mt-5 w-full h-11 rounded-lg border border-slate-300 text-xs font-bold text-slate-600 hover:border-[#005EB8] hover:text-[#005EB8] transition-colors">
              {patient.status === "discharged" ? "Reopen case" : "Mark as discharged"}
            </button>
            {user?.role === "admin" && (
              <button onClick={() => setDeleteOpen(true)} data-testid="delete-patient-btn"
                className="mt-2 w-full h-11 rounded-lg border border-red-200 text-xs font-bold text-[#D5281B] hover:bg-red-50 transition-colors">
                Delete patient record
              </button>
            )}
          </div>

          <TransportSection patient={patient} onChanged={load} />

          {patient.allergies?.length > 0 && (
            <div className="rounded-xl border-2 border-[#D5281B] bg-red-50 p-5" data-testid="patient-allergies-banner">
              <p className="flex items-center gap-2 font-heading font-bold text-[#D5281B] mb-2">
                <ShieldAlert className="h-5 w-5" /> ALLERGIES
              </p>
              <div className="flex flex-wrap gap-2">
                {patient.allergies.map((a) => (
                  <span key={a} className="bg-[#D5281B] text-white text-xs font-bold px-3 py-1.5 rounded-full">{a}</span>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="quick-actions">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">Quick actions</p>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setObsOpen(true)} data-testid="quick-action-observation"
                className="h-12 rounded-lg bg-[#007F3B] hover:bg-[#006631] transition-colors text-white text-xs font-bold inline-flex items-center justify-center gap-1.5">
                <Plus className="h-4 w-4" /> Observation
              </button>
              <button onClick={() => scrollToSection("medications-section")} data-testid="quick-action-medication"
                className="h-12 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white text-xs font-bold inline-flex items-center justify-center gap-1.5">
                <Pill className="h-4 w-4" /> Medication
              </button>
              <button onClick={() => scrollToSection("clinical-notes-section")} data-testid="quick-action-note"
                className="h-12 rounded-lg bg-[#00A499] hover:bg-[#00857c] transition-colors text-white text-xs font-bold inline-flex items-center justify-center gap-1.5">
                <Activity className="h-4 w-4" /> Add Note
              </button>
              <button onClick={() => scrollToSection("refusal-signatures-section")} data-testid="quick-action-refusal"
                className="h-12 rounded-lg bg-[#ED8B00] hover:bg-[#c97600] transition-colors text-white text-xs font-bold inline-flex items-center justify-center gap-1.5">
                <PenLine className="h-4 w-4" /> Refusal Form
              </button>
              <button onClick={() => setSgOpen(true)} data-testid="quick-action-safeguarding"
                className="col-span-2 h-12 rounded-lg bg-[#D5281B] hover:bg-[#b02015] transition-colors text-white text-xs font-bold inline-flex items-center justify-center gap-1.5">
                <ShieldAlert className="h-4 w-4" /> Safeguarding referral
              </button>
            </div>
          </div>
        </div>

        {/* Right: observations + notes */}
        <div className="lg:col-span-8 space-y-6">
          <ObservationsSection observations={observations} onNew={() => setObsOpen(true)} onEdit={setEditObs} />

          <MedicationsSection medications={medications} medForm={medForm} setMedForm={setMedForm}
            saving={saving} onSave={saveMedication} onDelete={deleteMedication} isAdmin={user?.role === "admin"}
            medOptions={medOptions} onMedListChanged={loadMeds} />

          <InjuriesSection injuries={patient.injuries || []} onToggle={toggleInjury} />

          <AdviceSection patient={patient} onChanged={load} />

          <EcgSection patient={patient} onChanged={load} />

          <NotesSection notes={notes} noteText={noteText} setNoteText={setNoteText}
            saving={saving} onSave={saveNote} onDelete={deleteNote} isAdmin={user?.role === "admin"} />

          <SignaturesSection signatures={signatures} sigName={sigName} setSigName={setSigName}
            sigNote={sigNote} setSigNote={setSigNote} sigData={sigData} setSigData={setSigData}
            saving={saving} onSave={saveSignature} />
        </div>
      </main>

      <Dialog open={sgOpen} onOpenChange={setSgOpen}>
        <DialogContent className="max-w-md" data-testid="safeguarding-dialog">
          <h2 className="font-heading text-xl font-bold text-[#D5281B] flex items-center gap-2">
            <ShieldAlert className="h-5 w-5" /> Safeguarding referral
          </h2>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">What are your concerns?</label>
              <textarea rows={3} value={sgForm.concerns} onChange={(e) => setSgForm((f) => ({ ...f, concerns: e.target.value }))}
                data-testid="sg-concerns" placeholder="Describe the safeguarding concern…"
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Who is involved? (optional)</label>
              <textarea rows={2} value={sgForm.who_involved} onChange={(e) => setSgForm((f) => ({ ...f, who_involved: e.target.value }))}
                data-testid="sg-who" placeholder="People involved or present…"
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Action taken (optional)</label>
              <textarea rows={2} value={sgForm.action_taken} onChange={(e) => setSgForm((f) => ({ ...f, action_taken: e.target.value }))}
                data-testid="sg-action" placeholder="What you did at the scene…"
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
            </div>
            <p className="text-[11px] text-slate-400">Saved on the patient's record and emailed to the office inbox straight away.</p>
            <button onClick={saveSafeguarding} disabled={saving || !sgForm.concerns.trim()} data-testid="sg-submit"
              className="w-full h-12 rounded-lg bg-[#D5281B] hover:bg-[#b02015] disabled:opacity-50 transition-colors text-white text-sm font-bold">
              {saving ? "Submitting…" : "Submit referral"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dischargeOpen} onOpenChange={setDischargeOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#0F2547]">Discharge patient</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Record the outcome for {patient.first_name} {patient.last_name}.
            </DialogDescription>
          </DialogHeader>
          <select value={dischargeOutcome} onChange={(e) => setDischargeOutcome(e.target.value)} data-testid="discharge-outcome-select"
            className="w-full h-12 rounded-lg border border-slate-300 px-3 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#005EB8] bg-white">
            <option value="returned_to_event">Returned to event</option>
            <option value="left_scene">Left scene (own accord)</option>
            <option value="taken_to_hospital">Taken to hospital</option>
            <option value="refused_care">Refused care / self-discharged</option>
            <option value="other">Other</option>
          </select>
          <div className="flex justify-end gap-3 mt-4">
            <button onClick={() => setDischargeOpen(false)}
              className="h-12 px-5 rounded-lg border border-slate-300 text-sm font-semibold text-slate-600">Cancel</button>
            <button onClick={confirmDischarge} data-testid="confirm-discharge-button"
              className="h-12 px-6 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white text-sm font-semibold">Confirm discharge</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#D5281B]">Delete patient record?</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              This permanently removes {patient.first_name} {patient.last_name} and all their observations, notes, medications and signatures. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-2">
            <button onClick={() => setDeleteOpen(false)} data-testid="cancel-delete-patient-button"
              className="h-12 px-5 rounded-lg border border-slate-300 text-sm font-semibold text-slate-600">Keep patient</button>
            <button onClick={deletePatient} data-testid="confirm-delete-patient-button"
              className="h-12 px-6 rounded-lg bg-[#D5281B] hover:bg-[#b02015] transition-colors text-white text-sm font-semibold">Delete permanently</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={obsOpen} onOpenChange={setObsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#0F2547]">New Observation</DialogTitle>
          </DialogHeader>
          <ObservationForm onSubmit={saveObservation} onCancel={() => setObsOpen(false)} submitting={saving} />
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editObs)} onOpenChange={(v) => !v && setEditObs(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#0F2547]">Edit Observation</DialogTitle>
          </DialogHeader>
          {editObs && (
            <ObservationForm initial={editObs} onSubmit={saveObservationEdit} onCancel={() => setEditObs(null)} submitting={saving} />
          )}
        </DialogContent>
      </Dialog>

      {editPatient && (
        <AddPatientDialog open={editPatient} onOpenChange={setEditPatient} patient={patient}
          onSaved={() => { setEditPatient(false); load(); }} />
      )}
    </div>
  );
}
